import React, { type FC, useState } from 'react'
import styled from '@emotion/styled'
import { useNavigate } from 'react-router'
import { fundsManagementTracker } from '@mobi/account/Common/Analytics'
import { useHostContextFeature } from '@mobi/account/Hooks'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { CustomerServiceTelephone } from '@mobi/component-library/Base/Link'
import { colors, font } from '@mobi/component-library/Foundations'
import { AlertBox } from '@mobi/component-library/Overlays/AlertBox'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { toMoney } from '@mobi/utils/money'
import { DAILY_DEPOSIT_LIMIT_AMOUNT, DAILY_DEPOSIT_LIMIT_TRANSACTIONS } from './constants'
import { routes as depositLimitRoutes } from './routes'

type HelpTextProps = {
  paymentType: 'deposit' | 'withdrawal'
  inputValue?: number
  paymentLimits: {
    minimum: number
  }
  remainingPaymentAmount: number
  remainingPaymentTransactions: number
  isDisabled?: boolean
  isInfoIconShown?: boolean
  showDepositLimitLink?: boolean
  hasAvailableBalance?: boolean
}

type PaymentLimitAlertBoxProps = {
  isOpen: boolean
  onClose: () => void
}

type DepositLimitAlertBoxProps = PaymentLimitAlertBoxProps & {
  showDepositLimitLink?: boolean
  normalFontWeight?: boolean
}

type InfoIconProps = {
  showDepositLimitLink?: boolean
}

const DepositLimitAlertBox: FC<DepositLimitAlertBoxProps> = ({
  isOpen,
  onClose,
  showDepositLimitLink = true,
  normalFontWeight = false,
}) => {
  const navigate = useNavigate()
  return (
    <AlertBox
      title='Deposit Limits'
      text={
        <span style={normalFontWeight ? { fontWeight: 'normal' } : undefined}>
          Your remaining daily deposit limit includes any personal limits you've set and our maximum
          limits for deposit types. The daily maximum is{' '}
          {toMoney(DAILY_DEPOSIT_LIMIT_AMOUNT, { decimalPlaces: 0 })} and up to{' '}
          {DAILY_DEPOSIT_LIMIT_TRANSACTIONS} deposits. If you have any questions about your deposit
          limit, please contact us on <CustomerServiceTelephone />
        </span>
      }
      confirmText={showDepositLimitLink ? 'Set Deposit Limit' : undefined}
      confirmColor='primary'
      cancelText='Close'
      cancelColor='secondary_outline'
      isOpen={isOpen}
      onConfirm={
        showDepositLimitLink ? () => navigate(depositLimitRoutes.editDepositLimit) : undefined
      }
      onCancel={onClose}
    />
  )
}

const WithdrawalLimitAlertBox: FC<PaymentLimitAlertBoxProps> = ({ isOpen, onClose }) => {
  return (
    <AlertBox
      title='Withdrawal Limits'
      text={
        <span>
          Your daily limit for withdrawals is $10,000 and up to 3 withdrawal transactions. If you
          have any questions about your withdrawal limit, please contact us on{' '}
          <CustomerServiceTelephone
            onClick={() =>
              fundsManagementTracker({
                interaction_type: 'Need Assistance',
                location: 'Withdrawals',
              })
            }
          />
        </span>
      }
      cancelText='Close'
      cancelColor='primary'
      isOpen={isOpen}
      onCancel={onClose}
    />
  )
}

const getPaymentLimitHelpText = ({
  paymentType,
  isPaymentValueVolumeLimitsEnabled,
  inputValue = 0,
  paymentLimits,
  remainingPaymentAmount,
  remainingPaymentTransactions,
  infoIcon,
  hasAvailableBalance = true,
}: Omit<HelpTextProps, 'isDisabled' | 'isInfoIconShown' | 'showDepositLimitLink'> & {
  isPaymentValueVolumeLimitsEnabled: boolean
  infoIcon?: React.ReactNode
}): string | React.ReactNode => {
  if (!isPaymentValueVolumeLimitsEnabled) {
    return inputValue > 0 && inputValue < paymentLimits.minimum
      ? `Minimum $${paymentLimits.minimum.toFixed(2)}`
      : `Enter custom amount`
  }

  if (hasAvailableBalance && (remainingPaymentAmount <= 0 || remainingPaymentTransactions <= 0)) {
    return `Daily ${paymentType} limit reached`
  }

  if (inputValue > 0 && inputValue < paymentLimits.minimum) {
    return `Minimum $${paymentLimits.minimum.toFixed(2)}`
  }

  if (inputValue > remainingPaymentAmount && remainingPaymentAmount > 0) {
    return (
      <span style={{ color: colors.error[600] }}>
        Please reduce your amount to below <strong>{toMoney(remainingPaymentAmount)}</strong>
      </span>
    )
  }

  const shouldPluralise = remainingPaymentTransactions !== 1
  const paymentTypeLabel = shouldPluralise ? `${paymentType}s` : paymentType

  return (
    <>
      <strong>{toMoney(remainingPaymentAmount)}</strong> and{' '}
      <strong>{remainingPaymentTransactions}</strong>&nbsp;{paymentTypeLabel} remaining {infoIcon}
    </>
  )
}

export const PaymentLimitHelpText: FC<HelpTextProps> = ({
  paymentType,
  inputValue,
  paymentLimits,
  remainingPaymentAmount,
  remainingPaymentTransactions,
  isDisabled = false,
  isInfoIconShown = true,
  showDepositLimitLink = true,
  hasAvailableBalance = true,
}) => {
  const isPaymentValueVolumeLimitsEnabled = useHostContextFeature(
    FeatureFlags.DEPOSIT_VALUE_VOLUME_LIMITS.key
  )
  const [showInfoModal, setShowInfoModal] = useState(false)

  const infoIcon = isInfoIconShown ? (
    <Icon name='LineInfoCircle' size='1.6rem' onClick={() => setShowInfoModal(true)} />
  ) : undefined

  const helpText = getPaymentLimitHelpText({
    paymentType,
    isPaymentValueVolumeLimitsEnabled,
    inputValue,
    paymentLimits,
    remainingPaymentAmount,
    remainingPaymentTransactions,
    infoIcon,
    hasAvailableBalance,
  })

  return (
    <>
      <TextStyled style={{ color: isDisabled ? colors.neutral[400] : colors.neutral[700] }}>
        {helpText}
      </TextStyled>
      {showInfoModal && paymentType === 'deposit' && (
        <DepositLimitAlertBox
          isOpen={showInfoModal}
          onClose={() => setShowInfoModal(false)}
          showDepositLimitLink={showDepositLimitLink}
        />
      )}
      {showInfoModal && paymentType === 'withdrawal' && (
        <WithdrawalLimitAlertBox isOpen={showInfoModal} onClose={() => setShowInfoModal(false)} />
      )}
    </>
  )
}

export const DepositLimitInfoIcon: FC<InfoIconProps> = ({ showDepositLimitLink = true }) => {
  const [showModal, setShowModal] = useState(false)

  return (
    <>
      <Icon name='LineInfoCircle' size='1.6rem' onClick={() => setShowModal(true)} />
      {showModal && (
        <DepositLimitAlertBox
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          showDepositLimitLink={showDepositLimitLink}
          normalFontWeight
        />
      )}
    </>
  )
}

const TextStyled = styled.span({
  display: 'flex',
  textAlign: 'center',
  alignItems: 'center',
  fontSize: 'inherit',
  lineHeight: 'inherit',
  fontFamily: font.family.primary,
  whiteSpace: 'pre',
})
